import React, { useEffect, useState } from 'react';
import { Descriptions, List, Typography, Button, Spin, Input, Select, Space, Tag, Tabs, Popconfirm, Modal, DatePicker } from 'antd';
import { toast } from '../../../lib/toast';
import Drawer from '../../../components/AppDrawer';
import dayjs from 'dayjs';
import type { QuoteDetails, QuoteStatus } from '../types';
import {
  QUOTE_STATUS_DRAFT,
  QUOTE_STATUS_REVISION_REQUESTED,
} from '../types';
import {
  acceptQuote,
  cancelQuote,
  downloadQuoteFile,
  expireQuote,
  rejectQuote,
  requestRevision,
  sendQuote,
  updateQuote,
} from '../api/documents';
import LeadViewDrawer from '../../leads/components/LeadViewDrawer';
import AuditProperties from '../../../components/AuditProperties';
import { useLeadOptions } from '../../../hooks/options/useLeadOptions';
import { StatusHistoryTimeline } from './StatusHistoryTimeline';
import { RevisionHistoryTimeline } from './RevisionHistoryTimeline';

const { Text, Title } = Typography;

const STATUS_LABELS: Record<QuoteStatus, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'Accepted',
  3: 'Rejected',
  4: 'Revision Requested',
  5: 'Expired',
  6: 'Cancelled',
};

const STATUS_COLORS: Record<QuoteStatus, string> = {
  0: 'default',
  1: 'blue',
  2: 'green',
  3: 'red',
  4: 'orange',
  5: 'volcano',
  6: 'default',
};

const isEditable = (status: QuoteStatus): boolean =>
  status === QUOTE_STATUS_DRAFT || status === QUOTE_STATUS_REVISION_REQUESTED;

const isTerminal = (status: QuoteStatus): boolean =>
  status === 2 || status === 3 || status === 5 || status === 6;

export interface DocumentDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetails | null | undefined;
  loading: boolean;
  onUpdated?: () => void;
}

export const DocumentDetailsDrawer: React.FC<DocumentDetailsDrawerProps> = ({
  open,
  onClose,
  quote,
  loading,
  onUpdated,
}) => {

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<string | undefined>();
  const [leadId, setLeadId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const [transacting, setTransacting] = useState(false);
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionNoteInput, setRevisionNoteInput] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReasonInput, setRejectReasonInput] = useState('');
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReasonInput, setCancelReasonInput] = useState('');

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptExpiryDate, setAcceptExpiryDate] = useState<dayjs.Dayjs | null>(null);

  const { data: leads = [], isLoading: loadingLeads } = useLeadOptions();

  useEffect(() => {
    if (!open || !quote) return;
    setIsEditing(false);
    setTitle(quote.title);
    setDescription(quote.description ?? undefined);
    setLeadId(quote.leadId);
    setSelectedLeadId(null);
    setShowRevisionForm(false);
    setRevisionNoteInput('');
    setShowRejectForm(false);
    setRejectReasonInput('');
    setShowCancelForm(false);
    setCancelReasonInput('');
    setShowAcceptModal(false);
    setAcceptExpiryDate(null);
  }, [open, quote]);

  function formatDateTime(s: string | null | undefined): React.ReactNode {
    if (s == null || s === '') return '';
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = d.getHours() % 12 || 12;
      const min = String(d.getMinutes()).padStart(2, '0');
      const ampm = d.getHours() < 12 ? 'AM' : 'PM';
      return <span title={`${y}/${m}/${day} ${String(h).padStart(2, '0')}:${min} ${ampm}`}>{y}/{m}/{day}</span>;
    } catch {
      return s ?? '';
    }
  }

  async function handleSave() {
    if (!quote) return;
    if (!title.trim()) {
      toast.error('Title is required.');
      return;
    }
    setSaving(true);
    try {
      await updateQuote(quote.id, {
        title: title.trim(),
        description: description ?? null,
        leadId,
      });
      toast.success('Document updated.');
      setIsEditing(false);
      onUpdated?.();
    } catch (err: unknown) {
      const detail: string | undefined =
        (err as { response?: { data?: { detail?: string } } } | null | undefined)?.response
          ?.data?.detail ?? 'Failed to update document.';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  }

  async function handleTransition(action: () => Promise<void>, successMsg: string) {
    setTransacting(true);
    try {
      await action();
      toast.success(successMsg);
      onUpdated?.();
    } catch (err: unknown) {
      const detail: string | undefined =
        (err as { response?: { data?: { detail?: string } } } | null | undefined)?.response
          ?.data?.detail ?? 'Action failed.';
      toast.error(detail);
    } finally {
      setTransacting(false);
    }
  }

  async function handleRequestRevision() {
    if (!quote) return;
    setTransacting(true);
    try {
      await requestRevision(quote.id, revisionNoteInput.trim() || undefined);
      toast.success('Revision requested.');
      setShowRevisionForm(false);
      setRevisionNoteInput('');
      onUpdated?.();
    } catch (err: unknown) {
      const detail: string | undefined =
        (err as { response?: { data?: { detail?: string } } } | null | undefined)?.response
          ?.data?.detail ?? 'Failed to request revision.';
      toast.error(detail);
    } finally {
      setTransacting(false);
    }
  }

  async function handleRejectWithReason() {
    if (!quote) return;
    setTransacting(true);
    try {
      await rejectQuote(quote.id, rejectReasonInput.trim() || undefined);
      toast.success('Document rejected.');
      setShowRejectForm(false);
      setRejectReasonInput('');
      onUpdated?.();
    } catch (err: unknown) {
      const detail: string | undefined =
        (err as { response?: { data?: { detail?: string } } } | null | undefined)?.response
          ?.data?.detail ?? 'Failed to reject document.';
      toast.error(detail);
    } finally {
      setTransacting(false);
    }
  }

  async function handleCancelWithReason() {
    if (!quote) return;
    setTransacting(true);
    try {
      await cancelQuote(quote.id, cancelReasonInput.trim() || undefined);
      toast.success('Document cancelled.');
      setShowCancelForm(false);
      setCancelReasonInput('');
      onUpdated?.();
    } catch (err: unknown) {
      const detail: string | undefined =
        (err as { response?: { data?: { detail?: string } } } | null | undefined)?.response
          ?.data?.detail ?? 'Failed to cancel document.';
      toast.error(detail);
    } finally {
      setTransacting(false);
    }
  }

  function getErrorMessage(err: unknown, fallback: string): string {
    const response = (err as { response?: { data?: Record<string, unknown> } } | null | undefined)?.response;
    const data = response?.data;
    if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      return (data.errors as Array<{ description: string }>).map(e => e.description).join('; ');
    }
    if (typeof data?.detail === 'string' && data.detail) {
      return data.detail;
    }
    return fallback;
  }

  async function handleAccept() {
    if (!quote) return;
    if (acceptExpiryDate && acceptExpiryDate.isBefore(dayjs(), 'day')) {
      toast.error('Expiry date must be in the future.');
      return;
    }
    setTransacting(true);
    try {
      await acceptQuote(quote.id, acceptExpiryDate?.toISOString() ?? null);
      toast.success('Document accepted. Lead marked as Closed Won.');
      setShowAcceptModal(false);
      setAcceptExpiryDate(null);
      onUpdated?.();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Failed to accept document.'));
    } finally {
      setTransacting(false);
    }
  }

  function renderActionButtons(q: QuoteDetails) {
    if (isTerminal(q.status)) return null;

    return (
      <div className="cp-form-section">
        <Text strong>Actions</Text>
        <div style={{ marginTop: 12 }}>
          {q.status === 0 && (
            <Space wrap direction="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Popconfirm
                  title="Send this document to the client?"
                  onConfirm={() => handleTransition(() => sendQuote(q.id), 'Document sent.')}
                  okText="Mark as send"
                  cancelText="No"
                >
                  <Button type="primary" loading={transacting}>
                    Mark as send
                  </Button>
                </Popconfirm>
                <Button
                  danger
                  onClick={() => setShowCancelForm((v) => !v)}
                  disabled={transacting}
                >
                  Cancel
                </Button>
              </Space>
              {showCancelForm && (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Reason for cancellation (optional)"
                    value={cancelReasonInput}
                    onChange={(e) => setCancelReasonInput(e.target.value)}
                    maxLength={2000}
                  />
                  <Space>
                    <Button
                      danger
                      onClick={handleCancelWithReason}
                      loading={transacting}
                    >
                      Confirm Cancellation
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCancelForm(false);
                        setCancelReasonInput('');
                      }}
                      disabled={transacting}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Space>
              )}
            </Space>
          )}

          {q.status === 1 && (
            <Space wrap direction="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Button
                  type="primary"
                  loading={transacting}
                  onClick={() => setShowAcceptModal(true)}
                >
                  Accept
                </Button>
                <Button
                  danger
                  onClick={() => setShowRejectForm((v) => !v)}
                  disabled={transacting}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => setShowRevisionForm((v) => !v)}
                  disabled={transacting}
                >
                  Request Revision
                </Button>
                <Popconfirm
                  title="Mark this document as expired?"
                  onConfirm={() => handleTransition(() => expireQuote(q.id), 'Document expired.')}
                  okText="Expire"
                  cancelText="No"
                >
                  <Button loading={transacting}>Expire</Button>
                </Popconfirm>
                <Button
                  onClick={() => setShowCancelForm((v) => !v)}
                  disabled={transacting}
                >
                  Cancel
                </Button>
              </Space>
              {showRejectForm && (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Reason for rejection (optional)"
                    value={rejectReasonInput}
                    onChange={(e) => setRejectReasonInput(e.target.value)}
                    maxLength={2000}
                  />
                  <Space>
                    <Button
                      type="primary"
                      danger
                      onClick={handleRejectWithReason}
                      loading={transacting}
                    >
                      Confirm Rejection
                    </Button>
                    <Button
                      onClick={() => {
                        setShowRejectForm(false);
                        setRejectReasonInput('');
                      }}
                      disabled={transacting}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Space>
              )}
              {showRevisionForm && (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Describe what changes the client requested (optional)"
                    value={revisionNoteInput}
                    onChange={(e) => setRevisionNoteInput(e.target.value)}
                    maxLength={2000}
                  />
                  <Space>
                    <Button
                      type="primary"
                      onClick={handleRequestRevision}
                      loading={transacting}
                    >
                      Confirm Revision Request
                    </Button>
                    <Button
                      onClick={() => {
                        setShowRevisionForm(false);
                        setRevisionNoteInput('');
                      }}
                      disabled={transacting}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Space>
              )}
              {showCancelForm && (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Reason for cancellation (optional)"
                    value={cancelReasonInput}
                    onChange={(e) => setCancelReasonInput(e.target.value)}
                    maxLength={2000}
                  />
                  <Space>
                    <Button
                      danger
                      onClick={handleCancelWithReason}
                      loading={transacting}
                    >
                      Confirm Cancellation
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCancelForm(false);
                        setCancelReasonInput('');
                      }}
                      disabled={transacting}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Space>
              )}
            </Space>
          )}

          {q.status === 4 && (
            <Space wrap direction="vertical" style={{ width: '100%' }}>
              <Space wrap>
                <Popconfirm
                  title="Re-send this document to the client?"
                  onConfirm={() => handleTransition(() => sendQuote(q.id), 'Document re-sent.')}
                  okText="Mark as send"
                  cancelText="No"
                >
                  <Button type="primary" loading={transacting}>
                    Mark as send
                  </Button>
                </Popconfirm>
                <Button
                  danger
                  onClick={() => setShowCancelForm((v) => !v)}
                  disabled={transacting}
                >
                  Cancel
                </Button>
              </Space>
              {showCancelForm && (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="Reason for cancellation (optional)"
                    value={cancelReasonInput}
                    onChange={(e) => setCancelReasonInput(e.target.value)}
                    maxLength={2000}
                  />
                  <Space>
                    <Button
                      danger
                      onClick={handleCancelWithReason}
                      loading={transacting}
                    >
                      Confirm Cancellation
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCancelForm(false);
                        setCancelReasonInput('');
                      }}
                      disabled={transacting}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Space>
              )}
            </Space>
          )}
        </div>
      </div>
    );
  }

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <Title level={5} style={{ margin: 0 }}>
            Document details
          </Title>
          {quote && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              #{quote.id}
            </Text>
          )}
        </Space>
      }
      placement="right"
      size="large"
      open={open}
      onClose={onClose}
      extra={
        quote && isEditable(quote.status)
          ? isEditing
            ? (
              <Space>
                <Button
                  onClick={() => {
                    if (!quote) return;
                    setIsEditing(false);
                    setTitle(quote.title);
                    setDescription(quote.description ?? undefined);
                    setLeadId(quote.leadId);
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="primary" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </Space>
            )
            : (
              <Button type="primary" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )
          : null
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : quote == null ? (
        <Text type="secondary">Document not found.</Text>
      ) : (
        <>
          <div className="cp-form-section">
            {isEditing ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text strong>Title</Text>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    style={{ marginTop: 4 }}
                  />
                </div>
                <div>
                  <Text strong>Description</Text>
                  <Input.TextArea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value || undefined)}
                    style={{ marginTop: 4 }}
                    placeholder="Optional notes or summary for this document"
                  />
                </div>
                <div>
                  <Text strong>Lead</Text>
                  <Select<string | null>
                    style={{ width: '100%', marginTop: 4 }}
                    allowClear
                    placeholder="Select a lead"
                    loading={loadingLeads}
                    value={leadId}
                    onChange={(value) => setLeadId(value)}
                    options={leads.map((l) => ({
                      value: l.id,
                      label: l.label,
                    }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </div>
              </Space>
            ) : (
              <Descriptions column={2} size="small" title="Document">
                <Descriptions.Item label="Title" span={2}>
                  {quote.title}
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={2}>
                  {quote.description ?? ''}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={STATUS_COLORS[quote.status]}>
                    {STATUS_LABELS[quote.status]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Lead">
                  {quote.leadId ? (
                    <Typography.Link onClick={() => setSelectedLeadId(quote.leadId)}>
                      {quote.leadTitle ?? 'View'}
                    </Typography.Link>
                  ) : (
                    <Text type="secondary">—</Text>
                  )}
                </Descriptions.Item>
                {quote.validUntilDate && (
                  <Descriptions.Item label="Valid until" span={2}>
                    {formatDateTime(quote.validUntilDate)}
                  </Descriptions.Item>
                )}
                {quote.sentAtUtc && (
                  <>
                    <Descriptions.Item label="Sent at">
                      {formatDateTime(quote.sentAtUtc)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sent by">
                      {quote.sentByDisplayName ?? quote.sentByUserId ?? ''}
                    </Descriptions.Item>
                  </>
                )}
                {quote.acceptedAtUtc && (
                  <Descriptions.Item label="Accepted at" span={2}>
                    {formatDateTime(quote.acceptedAtUtc)}
                  </Descriptions.Item>
                )}
                {quote.acceptedExpiryDate && (
                  <Descriptions.Item label="Accepted expiry" span={2}>
                    {formatDateTime(quote.acceptedExpiryDate)}
                  </Descriptions.Item>
                )}
                {quote.rejectedAtUtc && (
                  <Descriptions.Item label="Rejected at" span={2}>
                    {formatDateTime(quote.rejectedAtUtc)}
                  </Descriptions.Item>
                )}
                {quote.cancelledAtUtc && (
                  <Descriptions.Item label="Cancelled at" span={2}>
                    {formatDateTime(quote.cancelledAtUtc)}
                  </Descriptions.Item>
                )}
                {quote.expiredAtUtc && (
                  <Descriptions.Item label="Expired at" span={2}>
                    {formatDateTime(quote.expiredAtUtc)}
                  </Descriptions.Item>
                )}
                {quote.revisionNote && (
                  <Descriptions.Item label="Revision note" span={2}>
                    {quote.revisionNote}
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
          </div>

          {!isEditing && renderActionButtons(quote)}

          <div className="cp-form-section">
            <Text strong>Files</Text>
            <List
              style={{ marginTop: 8 }}
              dataSource={quote.files}
              locale={{ emptyText: 'No files attached.' }}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    <Button
                      key="download"
                      type="link"
                      onClick={() => downloadQuoteFile(quote.id, file.blobId)}
                    >
                      Download
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={file.originalFileName}
                    description={`${(file.sizeBytes / 1024).toFixed(1)} KB • ${formatDateTime(
                      file.createdAtUtc
                    )}`}
                  />
                </List.Item>
              )}
            />
          </div>

          {!isEditing && (
            <div className="cp-form-section">
              <Tabs
                items={[
                  {
                    key: 'status',
                    label: 'Status History',
                    children: <StatusHistoryTimeline items={quote.statusHistory} />,
                  },
                  {
                    key: 'revision',
                    label: 'Revision History',
                    children: <RevisionHistoryTimeline items={quote.revisionHistory} />,
                  },
                ]}
              />
            </div>
          )}

          <AuditProperties
            className="cp-form-section"
            values={{
              createdAtUtc: quote.createdAtUtc,
              createdByDisplayName: quote.createdByDisplayName,
              createdByUserId: quote.createdByUserId,
              lastModifiedAtUtc: quote.lastModifiedAtUtc,
              modifiedByDisplayName: quote.modifiedByDisplayName,
              modifiedByUserId: quote.modifiedByUserId,
            }}
          />

          <Modal
            title="Accept Document"
            open={showAcceptModal}
            onOk={handleAccept}
            onCancel={() => {
              setShowAcceptModal(false);
              setAcceptExpiryDate(null);
            }}
            okText="Accept"
            confirmLoading={transacting}
          >
            <p>Mark this document as accepted by the client?</p>
            <div style={{ marginTop: 16 }}>
              <Typography.Text>Expiry Date (optional)</Typography.Text>
              <DatePicker
                style={{ width: '100%', marginTop: 4 }}
                allowClear
                placeholder="Select expiry date"
                value={acceptExpiryDate}
                onChange={(date) => setAcceptExpiryDate(date)}
                disabledDate={(current) => current.isBefore(dayjs(), 'day')}
              />
            </div>
          </Modal>

          <LeadViewDrawer
            open={selectedLeadId != null}
            onClose={() => setSelectedLeadId(null)}
            leadId={selectedLeadId}
          />
        </>
      )}
    </Drawer>
  );
};
